/**
 * Area-mode lifecycle.
 *
 * The polygonSelection tool is a *drawing* tool — it places a polygon and then
 * hands off to area-mode, which is the persistent UI **state** that owns:
 *   - the polygon entity in the viewer (not removed until area-mode exits)
 *   - the fetched section info / loadcase datasources / active loadcases
 *   - the hover-info tooltip subscription
 *
 * Crucially, area-mode is **not** a tool: it doesn't lock Cesium input. Normal
 * entity selection, the Toolbar, and other map tools all stay available while
 * a polygon is drawn. The polygon and section data simply persist until the
 * user explicitly exits.
 *
 * State lives in `area-mode-state.svelte.ts` as a runes-based singleton.
 * This module is the orchestration layer that ties together that state with
 * the Cesium viewer, IDB cache, broadcast channel and DOM listeners.
 */

import { get } from 'svelte/store';

import { useCesiumViewer } from '../cesium-viewer';
import {
	activeTool,
	isAnyToolEnabled,
	mapToolsStore,
	type ToolPropertyKey
} from '../map-tools-store';
import { toggleEscapeToolListener } from '../map-tools';
import { confirmModal } from '$lib/components/organisms/confirmModal/confirmModal';
import { clearLoadCases } from './areaToolLoadCases.svelte';
import { activateHoverInfo, deactivateHoverInfo } from './areaToolHoverInfo.svelte';
import { runManualMapRefresh } from '../map-utils';
import { dismissOverlayMessageByKey } from '$lib/stores/cesium-overlay-messages-store';
import { areaModeState } from './area-mode-state.svelte';
// Imported for side-effect: installs module-level `$effect.root` watchers
// that auto-fetch `/info` and apply section colours + mast labels reactively.
import './area-mode-effects.svelte';
// Imported for side-effect: installs a `$effect.root` watcher that loads the
// polygon's TerrainProfile packets into the shared distance-analysis datasource
// and drives the terrain-offset lines while "Vis avstander" is active.
import './areaToolTerrainProfiles.svelte';
import type { Entity, Viewer } from 'cesium';

/**
 * Shared dedupe key for the overlay message that warns the user that section
 * data is out-of-date after a montagestretch save. Defined here so the
 * area-mode lifecycle can clean it up on exit without coupling to the panel.
 */
export const AREA_MODE_STALE_DATA_OVERLAY_KEY = 'area-mode-section-data-stale';

let installed = false;
/**
 * Tools that mutate underlying entities while area-mode is active. Finishing
 * one of these triggers a section-info refresh because the polygon's contents
 * may have changed. Other Toolbar tools (e.g. `createTerrainProfileFromPoles`)
 * are read-only with respect to mastpoints/lines, so we leave area-mode alone
 * when they end — otherwise the chip would briefly snap to "0 master · 0 m²"
 * while a fresh /info request reloaded.
 */
const ENTITY_EDITING_SUBTOOLS: ReadonlySet<ToolPropertyKey> = new Set([
	'rotateMastpoint',
	'quickMoveMastPoint',
	'quickMovePole',
	'quickMovePoleConstruction',
	'quickAdjustMastPointHeight'
]);

/**
 * The most recently observed entity-editing sub-tool. Reset when area-mode
 * itself toggles off, or after we run the resume side-effects.
 */
let lastEditingSubtool: ToolPropertyKey | null = null;

// ── Public API ──────────────────────────────────────────────────────────────

export type AreaModeActivationOptions = {
	polygonEntity: Entity;
	mastpoints: Entity[];
	polygonLngLat: [number, number][];
	polygon: { type: 'Polygon'; coordinates: [number, number][][] };
	/**
	 * Initial state of the per-section "Vis avstander" toggle. The polygon tool
	 * passes `true` when the global distance view was on as drawing started, so
	 * the measurement carries over seamlessly. Defaults to `false`.
	 */
	showDistances?: boolean;
};

/**
 * Called by polygonSelectionTool.finalisePolygon once the polygon is closed.
 * Takes ownership of the polygon entity and flips the area-mode state on.
 */
export function activateAreaMode(opts: AreaModeActivationOptions): void {
	const viewer = useCesiumViewer();
	if (!viewer) return;

	// selectedMapEntities stays on mapToolsStore — it's shared across many
	// tools/UIs and isn't owned by area-mode in particular.
	mapToolsStore.setSelectedMapEntities(opts.mastpoints);

	areaModeState.active = true;
	areaModeState.polygon = opts.polygon;
	areaModeState.polygonEntity = opts.polygonEntity;
	areaModeState.setBoundariesAndArea(opts.polygonLngLat);
	areaModeState.sectionInfo = null;
	areaModeState.sectionInfoError = null;
	areaModeState.sectionInfoLoading = false;
	areaModeState.activeLoadCases = [];
	areaModeState.showDistances = opts.showDistances ?? false;
	areaModeState.panelMinimized = false;

	// polygonSelection (drawing) is done — tear it down.
	mapToolsStore.update((store) => {
		store.polygonSelection.toolEnabled = false;
		store.polygonSelection.deactivateTool = () => {};
		return store;
	});

	activateHoverInfo();

	// Esc → confirm-and-exit. Routed via a dedicated listener (not the shared
	// tool-Esc handler) because area-mode isn't a tool — the shared listener
	// would never fire for it.
	toggleEscapeToolListener({ toggleOn: false });
	document.addEventListener('keydown', handleAreaModeEsc, false);

	ensureSubtoolWatcher();
}

/** Confirm + deactivate. Wired to Esc and the "Lukk" button. */
export function requestExitAreaMode(): void {
	confirmModal(
		'Er du sikker på at du vil gå ut av områdemodus. Områdemarkeringen vil bli fjernet og ulagret data vil gå tapt.',
		() => {
			deactivateAreaMode();
		},
		undefined,
		{
			labels: {
				confirm: 'Ja, gå ut av områdemodus',
				cancel: 'Nei, ta meg tilbake.',
				title: 'Avslutt områdemodus'
			},
			type: 'warning'
		}
	);
}

/** Hard exit — remove polygon, clear state. */
export function deactivateAreaMode(): void {
	const viewer = useCesiumViewer();
	if (!viewer) return;

	clearLoadCases();
	deactivateHoverInfo();
	removePolygonEntity(viewer);

	areaModeState.reset();
	mapToolsStore.update((store) => {
		store.selectedMapEntities = [];
		return store;
	});

	dismissOverlayMessageByKey(AREA_MODE_STALE_DATA_OVERLAY_KEY);
	document.removeEventListener('keydown', handleAreaModeEsc, false);
}

// ── Minimize / maximize the side panel ─────────────────────────────────────

/**
 * Re-exported thin wrappers around the state mutators so callers don't need
 * to know whether the panel state lives on the class or somewhere else.
 */
export function minimizeAreaPanel(): void {
	areaModeState.minimize();
}

export function maximizeAreaPanel(): void {
	areaModeState.maximize();
}

// ── Sub-tool integration ────────────────────────────────────────────────────

/**
 * Called when the user activates a sub-tool (rotate / move / adjust-height)
 * from the Toolbar while area-mode is active. Suppresses hover-info so it
 * doesn't compete with the tool's own overlay; the sub-tool watcher restores
 * everything when the tool deactivates.
 */
export function pauseAreaModeForSubtool(): void {
	const viewer = useCesiumViewer();
	if (!viewer) return;
	deactivateHoverInfo();
	// The polygon panel hides automatically while any tool is active (its
	// mount in +page.svelte is in the no-tool branch).
}

/**
 * Called after a sub-tool deactivates. Re-enables hover-info — nothing else.
 *
 * Earlier versions also wiped `sectionInfo` + active load cases and re-marked
 * the cache stale here, on the assumption that "the underlying entities may
 * have changed". That was redundant *and* incorrect:
 *   - Redundant because every entity-editing mutation already routes through
 *     `handleContextualizedCzmlResponse` in `map-utils`, which wipes section
 *     state and marks the cache stale exactly when the affected entities
 *     overlap the polygon.
 *   - Incorrect because it threw away state the user had legitimately just
 *     refreshed (e.g. by clicking "Generer på nytt" in the report tab while
 *     the tool was still active — the broadcast had updated `sectionInfo` to
 *     a fresh /calculate result, and this wipe then dropped it back to the
 *     stiff /info refetch). The user-visible symptom was the load-case chip
 *     reverting to "Stiv beregning" the moment the sub-tool was exited.
 *
 * Mutations are the only thing that should mark the cache stale, and that
 * already happens at the source.
 */
function resumeAreaModeAfterSubtool(): void {
	activateHoverInfo();
}

/**
 * Reload the underlying map data and refetch section info, while keeping the
 * areaMode polygon (and the panel) intact. Used by the stale-data overlay
 * shown after a montagestretch save.
 *
 * Section info refetch is triggered indirectly by clearing `sectionInfo` —
 * the parent component's refresh callback then re-issues `/info` (keeping
 * the mutation hook in its component context).
 */
export async function refreshAreaModeContext(): Promise<void> {
	if (!areaModeState.active) return;
	await runManualMapRefresh();
	clearLoadCases();
	areaModeState.sectionInfo = null;
	areaModeState.sectionInfoError = null;
	areaModeState.sectionInfoLoading = false;
	areaModeState.activeLoadCases = [];
}

/**
 * Module-local watcher: when an *entity-editing* sub-tool finishes while
 * area-mode is active, run the resume side-effects (refresh section info,
 * mark cache stale). Non-editing tools (terrain profiles, layer pickers,
 * etc.) are ignored — their lifecycle would otherwise wipe and re-fetch
 * section info every time the user toggled them.
 */
function ensureSubtoolWatcher(): void {
	if (installed) return;
	installed = true;

	activeTool.subscribe(($activeTool) => {
		if (!areaModeState.active) {
			lastEditingSubtool = null;
			return;
		}
		if ($activeTool && ENTITY_EDITING_SUBTOOLS.has($activeTool)) {
			lastEditingSubtool = $activeTool;
			return;
		}
		if (!$activeTool && lastEditingSubtool) {
			lastEditingSubtool = null;
			void resumeAreaModeAfterSubtool();
		}
	});
}

// ── Internal ────────────────────────────────────────────────────────────────

function removePolygonEntity(viewer: Viewer): void {
	const entity = areaModeState.polygonEntity;
	if (entity) {
		try {
			viewer.entities.remove(entity);
		} catch {
			// already removed — ignore.
		}
	}
}

function handleAreaModeEsc(e: KeyboardEvent): void {
	if (e.key !== 'Escape' && e.key !== 'Esc') return;
	if (!areaModeState.active) return;
	// Defer to any active tool's own Esc handler; the watcher resumes us
	// when the tool deactivates.
	if (get(isAnyToolEnabled)) return;
	requestExitAreaMode();
}
